import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { unlockCommunityPredictionWithAck } from "./predictionSocket";

const authHeaders = () => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mergeUniqueById = (existing = [], incoming = []) => {
  const seen = new Set(existing.map((row) => String(row?._id)).filter(Boolean));
  const merged = [...existing];
  for (const row of incoming) {
    const id = row?._id != null ? String(row._id) : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(row);
  }
  return merged;
};

const resolveCommunityHasNext = ({ pagination, rows, limit, page, append, addedCount }) => {
  if (append && (!rows.length || addedCount === 0)) return false;
  if (typeof pagination?.hasNextPage === "boolean") return pagination.hasNextPage;
  if (typeof pagination?.totalPages === "number" && page > 0) {
    return page < pagination.totalPages;
  }
  return rows.length >= limit;
};

const resolveAIHasMore = ({ pagination, rows, limit, page, append, addedCount }) => {
  if (append && (!rows.length || addedCount === 0)) return false;
  if (typeof pagination?.hasMore === "boolean") return pagination.hasMore;
  if (typeof pagination?.totalPages === "number" && page > 0) {
    return page < pagination.totalPages;
  }
  return rows.length >= limit;
};

export const fetchCommunityPredictions = createAsyncThunk(
  "predictions/fetchCommunity",
  async ({ page = 1, limit = 20, append = false } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/predictions`, {
        params: { page, limit },
      });
      return { ...data, append };
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: "Failed to load community picks" });
    }
  }
);

export const searchCommunityPredictions = createAsyncThunk(
  "predictions/searchCommunity",
  async ({ query, limit = 20 }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/predictions/search`, {
        params: { query, limit },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: "Search failed" });
    }
  }
);

export const fetchAIPredictions = createAsyncThunk(
  "predictions/fetchAI",
  async ({ page = 1, limit = 20, search = "", append = false } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/predictions/ai`, {
        params: { page, limit, search: search || undefined },
        headers: authHeaders(),
      });
      return { ...data, append };
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: "Failed to load AI predictions" });
    }
  }
);

export const unlockAIPrediction = createAsyncThunk(
  "predictions/unlockAI",
  async (predictionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/predictions/ai/unlock/${predictionId}`,
        {},
        { headers: authHeaders() }
      );
      return { predictionId, ...data };
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: "Unlock failed" });
    }
  }
);

export const unlockCommunityPrediction = createAsyncThunk(
  "predictions/unlockCommunity",
  async ({ predictionId, userId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem(APP_TOKEN_KEY);
      if (!token) {
        return rejectWithValue({ error: "Authentication required." });
      }
      const data = await unlockCommunityPredictionWithAck({
        predictionId,
        userId,
        token,
      });
      return { predictionId, userId, ...data };
    } catch (err) {
      return rejectWithValue({ error: err.message || "Unlock failed" });
    }
  }
);

const initialState = {
  mode: "ai",
  community: [],
  communityLoading: false,
  communityLoadingMore: false,
  communityError: null,
  communityPagination: {
    page: 1,
    hasNextPage: false,
    totalPredictions: 0,
  },
  communitySearch: [],
  communitySearchLoading: false,
  ai: [],
  aiLoading: false,
  aiLoadingMore: false,
  aiError: null,
  aiPagination: {
    currentPage: 1,
    hasMore: false,
    totalItems: 0,
  },
  unlockPending: null,
  unlockError: null,
};

const predictionsSlice = createSlice({
  name: "predictions",
  initialState,
  reducers: {
    setPredictionsMode: (state, action) => {
      state.mode = action.payload;
    },
    clearCommunitySearch: (state) => {
      state.communitySearch = [];
    },
    clearPredictionsErrors: (state) => {
      state.communityError = null;
      state.aiError = null;
      state.unlockError = null;
    },
    addCommunityPrediction: (state, action) => {
      const row = action.payload;
      if (!row?._id) return;
      if (state.community.some((p) => p._id === row._id)) return;
      state.community = [row, ...state.community];
      state.communityPagination.totalPredictions =
        (state.communityPagination.totalPredictions || 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunityPredictions.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) state.communityLoadingMore = true;
        else state.communityLoading = true;
        state.communityError = null;
      })
      .addCase(fetchCommunityPredictions.fulfilled, (state, action) => {
        state.communityLoading = false;
        state.communityLoadingMore = false;
        const rows = action.payload.predictions || [];
        const limit = Number(action.meta.arg?.limit) || 20;
        const page = Number(action.payload.pagination?.page ?? action.meta.arg?.page ?? 1);
        const previousLength = state.community.length;
        const nextCommunity = action.payload.append
          ? mergeUniqueById(state.community, rows)
          : rows;
        const addedCount = nextCommunity.length - previousLength;
        const hasNextPage = resolveCommunityHasNext({
          pagination: action.payload.pagination,
          rows,
          limit,
          page,
          append: action.payload.append,
          addedCount,
        });
        state.community = nextCommunity;
        const totalPredictions =
          action.payload.pagination?.totalPredictions ?? state.community.length;
        state.communityPagination = {
          page,
          hasNextPage:
            hasNextPage &&
            (!totalPredictions || state.community.length < totalPredictions),
          totalPredictions,
        };
      })
      .addCase(fetchCommunityPredictions.rejected, (state, action) => {
        state.communityLoading = false;
        state.communityLoadingMore = false;
        state.communityError = action.payload;
      })
      .addCase(searchCommunityPredictions.pending, (state) => {
        state.communitySearchLoading = true;
      })
      .addCase(searchCommunityPredictions.fulfilled, (state, action) => {
        state.communitySearchLoading = false;
        state.communitySearch = action.payload.data || action.payload.predictions || [];
      })
      .addCase(searchCommunityPredictions.rejected, (state) => {
        state.communitySearchLoading = false;
        state.communitySearch = [];
      })
      .addCase(fetchAIPredictions.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) state.aiLoadingMore = true;
        else state.aiLoading = true;
        state.aiError = null;
      })
      .addCase(fetchAIPredictions.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiLoadingMore = false;
        const rows = action.payload.data || [];
        const limit = Number(action.meta.arg?.limit) || 20;
        const currentPage = Number(
          action.payload.pagination?.currentPage ?? action.meta.arg?.page ?? 1
        );
        const previousLength = state.ai.length;
        const nextAI = action.payload.append ? mergeUniqueById(state.ai, rows) : rows;
        const addedCount = nextAI.length - previousLength;
        const hasMore = resolveAIHasMore({
          pagination: action.payload.pagination,
          rows,
          limit,
          page: currentPage,
          append: action.payload.append,
          addedCount,
        });
        state.ai = nextAI;
        const totalItems = action.payload.pagination?.totalItems ?? state.ai.length;
        state.aiPagination = {
          currentPage,
          hasMore: hasMore && (!totalItems || state.ai.length < totalItems),
          totalItems,
        };
      })
      .addCase(fetchAIPredictions.rejected, (state, action) => {
        state.aiLoading = false;
        state.aiLoadingMore = false;
        state.aiError = action.payload;
      })
      .addCase(unlockAIPrediction.pending, (state, action) => {
        state.unlockPending = action.meta.arg;
        state.unlockError = null;
      })
      .addCase(unlockAIPrediction.fulfilled, (state, action) => {
        state.unlockPending = null;
        const id = String(action.payload.predictionId);
        state.ai = state.ai.map((p) =>
          String(p._id) === id ? { ...p, isUnlocked: true, requiresPayment: false } : p
        );
      })
      .addCase(unlockAIPrediction.rejected, (state, action) => {
        state.unlockPending = null;
        state.unlockError = action.payload;
      })
      .addCase(unlockCommunityPrediction.pending, (state, action) => {
        state.unlockPending = action.meta.arg?.predictionId;
        state.unlockError = null;
      })
      .addCase(unlockCommunityPrediction.fulfilled, (state, action) => {
        state.unlockPending = null;
        const id = String(action.payload.predictionId);
        const uid = action.payload.userId;
        state.community = state.community.map((p) => {
          if (String(p._id) !== id) return p;
          const unlockedBy = Array.isArray(p.unlockedBy) ? [...p.unlockedBy] : [];
          if (uid && !unlockedBy.some((x) => String(x) === String(uid))) {
            unlockedBy.push(uid);
          }
          return { ...p, unlockedBy, isUnlockedByUser: true };
        });
      })
      .addCase(unlockCommunityPrediction.rejected, (state, action) => {
        state.unlockPending = null;
        state.unlockError = action.payload;
      });
  },
});

export const { setPredictionsMode, clearCommunitySearch, clearPredictionsErrors, addCommunityPrediction } =
  predictionsSlice.actions;

export const selectPredictionsMode = (s) => s.predictions.mode;
export const selectCommunityPredictions = (s) => s.predictions.community;
export const selectCommunitySearch = (s) => s.predictions.communitySearch;
export const selectAIPredictions = (s) => s.predictions.ai;
export const selectCommunityLoading = (s) => s.predictions.communityLoading;
export const selectAILoading = (s) => s.predictions.aiLoading;
export const selectCommunityLoadingMore = (s) => s.predictions.communityLoadingMore;
export const selectAILoadingMore = (s) => s.predictions.aiLoadingMore;
export const selectCommunityPagination = (s) => s.predictions.communityPagination;
export const selectAIPagination = (s) => s.predictions.aiPagination;
export const selectPredictionsErrors = (s) => ({
  community: s.predictions.communityError,
  ai: s.predictions.aiError,
  unlock: s.predictions.unlockError,
});
export const selectUnlockPending = (s) => s.predictions.unlockPending;

export default predictionsSlice.reducer;
